import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation } from "@apollo/client";
import { PREPARE_RESERVATION_GUEST_MUTATION } from "globals/graphql/reservationGuest.graphql";
import { useAuth, useCurrentUser, useOperator } from "globals/hooks";
import type {
  PrepareReservationGuestInput,
  PrepareReservationGuestPayload,
} from "types";
import type {
  PaymentMethodSetupState,
  SavedPaymentMethod,
  SetupAttempt,
} from "components/payment/PaymentMethodSetup/types";
import {
  useReservationPaymentSetupStore,
  type PreparedReservationGuest,
} from "../context/ReservationPaymentSetupProvider";
import {
  createReservationFormErrorSchema,
  type CreateReservationFormState,
} from "../steps/confirm/components/CreateReservationForm/form/schemaValidation";
import {
  clearReservationSetupReturn,
  retainReservationSetup,
} from "../context/reservationSetupReturn";
import { createSetupKey } from "components/payment/PaymentMethodSetup/setupAttemptKey";
import { useCreateRequestContext } from "../context/useCreateRequestContext";
import { mergeConfirmRequestFormState } from "../context/helpers/mergeConfirmRequestFormState";
import type { ProfileSetupCorrelation } from "components/profile/paymentMethodSetupReturn";

export const getReservationGuestDetails = (
  bookingContact: CreateReservationFormState["bookingContact"]
) => {
  const contact =
    createReservationFormErrorSchema.fields.bookingContact.validateSync(
      bookingContact,
      { context: { isCreatingBookingContact: true } }
    );
  if (!contact.firstName.trim() || !contact.lastName.trim())
    throw new Error("Please complete your contact name.");
  return {
    firstName: contact.firstName.trim(),
    lastName: contact.lastName.trim(),
    email: contact.email.trim(),
    mobilePhone: contact.phoneFields.mobilePhone,
    phoneCountryCode: contact.phoneFields.phoneCountryCode,
    phoneCountryDialCode: contact.phoneFields.phoneCountryDialCode,
    phoneCountryName: contact.phoneFields.phoneCountryName,
    phoneCountryFormat: contact.phoneFields.phoneCountryFormat,
  };
};

export const useReservationPaymentSetup = (
  formState: CreateReservationFormState
) => {
  const { state, stateRef, ownerKeyRef, update, pendingRef, elementRef } =
    useReservationPaymentSetupStore();
  const { operator } = useOperator();
  const { authStage } = useAuth();
  const currentUser = useCurrentUser();
  const [request] = useCreateRequestContext();
  const formRef = useRef(formState);
  formRef.current = formState;
  const [prepare] = useMutation<
    { prepareReservationGuest: PrepareReservationGuestPayload },
    { input: PrepareReservationGuestInput }
  >(PREPARE_RESERVATION_GUEST_MUTATION);
  const [error, setError] = useState<string | null>(null);
  const [setupState, setSetupState] = useState<PaymentMethodSetupState | null>(
    null
  );
  const activeRef = useRef(true);
  useEffect(() => {
    activeRef.current = true;
    return () => {
      activeRef.current = false;
    };
  }, []);
  const ownerKey = state?.ownerKey;
  const checkoutContext = state?.checkoutContext;
  const isCurrent = useCallback(
    () =>
      !!ownerKey &&
      ownerKeyRef.current === ownerKey &&
      stateRef.current?.checkoutContext === checkoutContext,
    [ownerKey, checkoutContext, ownerKeyRef, stateRef]
  );
  const assertCurrent = useCallback(() => {
    if (!isCurrent())
      throw new Error("Checkout payer changed. Please try again.");
  }, [isCurrent]);

  const prepareGuest = useCallback(
    async (
      bookingContact: CreateReservationFormState["bookingContact"]
    ): Promise<PreparedReservationGuest> => {
      assertCurrent();
      const details = getReservationGuestDetails(bookingContact);
      const detailsKey = JSON.stringify(details);
      while (pendingRef.current?.ownerKey === ownerKey) {
        await pendingRef.current.promise;
        assertCurrent();
      }
      const setup = stateRef.current;
      if (
        setup.guest?.detailsKey === detailsKey &&
        Date.now() - setup.guest.preparedAt < 20 * 60 * 1000
      )
        return setup.guest;
      const pending = (async () => {
        const { data } = await prepare({
          variables: {
            input: {
              ...details,
              checkoutContext: setup.checkoutContext,
              preparationProof: setup.guest?.preparationProof,
            },
          },
        });
        assertCurrent();
        const result = data?.prepareReservationGuest?.result;
        if (result?.__typename !== "PrepareReservationGuestSuccess")
          throw new Error(
            result?.reason || "Unable to prepare checkout. Please try again."
          );
        const guest: PreparedReservationGuest = {
          contact: result.contact,
          preparationProof: result.preparationProof,
          detailsKey,
          preparedAt: Date.now(),
        };
        update({ ...stateRef.current, guest });
        if (activeRef.current) setError(null);
        return guest;
      })();
      pendingRef.current = { ownerKey, promise: pending };
      try {
        return await pending;
      } finally {
        if (pendingRef.current?.promise === pending) pendingRef.current = null;
      }
    },
    [assertCurrent, ownerKey, prepare, pendingRef, stateRef, update]
  );

  useEffect(() => {
    setError(null);
    setSetupState(null);
  }, [ownerKey]);

  const onAttemptCreated = useCallback(
    (attempt: SetupAttempt) => {
      assertCurrent();
      update({ ...stateRef.current, attempt });
    },
    [assertCurrent, stateRef, update]
  );
  const onSavedPaymentMethod = useCallback(
    (result: SavedPaymentMethod) => {
      assertCurrent();
      clearReservationSetupReturn();
      update({ ...stateRef.current, result });
    },
    [assertCurrent, stateRef, update]
  );
  const onStateChange = useCallback(
    (next: PaymentMethodSetupState) => {
      if (!activeRef.current || !isCurrent()) return;
      setSetupState(next);
      if (next.result) onSavedPaymentMethod(next.result);
    },
    [isCurrent, onSavedPaymentMethod]
  );

  const save = async (submittedForm: CreateReservationFormState) => {
    try {
      assertCurrent();
      if (authStage !== "authenticated")
        await prepareGuest(submittedForm.bookingContact);
      assertCurrent();
      if (!activeRef.current)
        throw new Error("Checkout was closed. Return to checkout to reserve.");
      const cached = stateRef.current.result;
      if (cached) return cached;
      if (!elementRef.current)
        throw new Error("Card form is still loading. Please try again.");
      const result = await elementRef.current.save({
        surface: "reservation",
        payerId:
          authStage === "authenticated"
            ? currentUser?.id
            : stateRef.current.guest?.contact.id,
        checkoutContext: stateRef.current.checkoutContext,
        preparationProof: stateRef.current.guest?.preparationProof,
      });
      assertCurrent();
      if (!activeRef.current)
        throw new Error("Checkout was closed. Return to checkout to reserve.");
      onSavedPaymentMethod(result);
      return result;
    } catch (failure) {
      if (activeRef.current && isCurrent())
        setError(
          failure instanceof Error
            ? failure.message
            : "Unable to save card. Please try again."
        );
      throw failure;
    }
  };

  const retain = (
    setupIntentId: string,
    profileSetup?: ProfileSetupCorrelation
  ) => {
    assertCurrent();
    retainReservationSetup(
      stateRef.current,
      mergeConfirmRequestFormState(request, formRef.current),
      setupIntentId,
      profileSetup,
      formRef.current
    );
  };
  const clearAddCardReturn = () => {
    assertCurrent();
    clearReservationSetupReturn();
    update({ ...stateRef.current, addCardReturn: undefined });
  };
  const restart = (keepGuest = false) => {
    assertCurrent();
    clearReservationSetupReturn();
    pendingRef.current = null;
    update({
      ...stateRef.current,
      checkoutContext: keepGuest
        ? stateRef.current.checkoutContext
        : createSetupKey(),
      attemptKey: createSetupKey(),
      guest: keepGuest ? stateRef.current.guest : undefined,
      attempt: undefined,
      resume: undefined,
      addCardReturn: undefined,
      result: undefined,
    });
    setError(null);
    setSetupState(null);
  };

  return {
    enabled: state?.enabled === true,
    state,
    stateRef,
    elementRef,
    error,
    setupState,
    isAuthenticated: authStage === "authenticated",
    savedPaymentMethods: authStage === "authenticated" ? currentUser?.paymentMethods || [] : undefined,
    payerId:
      authStage === "authenticated"
        ? currentUser?.id
        : state?.guest?.contact.id,
    operatorId: operator.id,
    currency: operator.settings?.currencyCode || "USD",
    billingEmail:
      authStage === "authenticated"
        ? currentUser?.email
        : formState.bookingContact?.email,
    onAttemptCreated,
    onStateChange,
    onSavedPaymentMethod,
    onBeforeConfirm: retain,
    onBeforeAddCardConfirm: (correlation: ProfileSetupCorrelation) =>
      retain(correlation.setupIntentId, correlation),
    clearAddCardReturn,
    restart,
    save,
  };
};

export type ReservationPaymentSetupAdapter = ReturnType<
  typeof useReservationPaymentSetup
>;
